'use client';

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@sola/ark-core/molecules/alert/alert.js';
import { ItemCardSkeleton } from '@sola/ark-core/molecules/item-card/item-card.js';
import { GraphQLCommitmentType } from '@sola/schema/dotcom/supporters/schema.generated.js';
import { useSupporterQuery } from '@sola/supporter-sdk/hooks/base.js';
import { commitmentsQuery } from '@sola/supporter-sdk/queries.js';

import { SponsorshipCard } from '../../molecules/sponsorship-card/sponsorship-card.js';

export const useComponentData = () => {
  const { data, isLoading, isFetching, isPending, isError } =
    useSupporterQuery(commitmentsQuery);

  const commitments = data ?? [];

  // Filter only active sponsorships (ChildSponsorship and LivingSponsorship)
  const sponsorships = useMemo(() => {
    if (!commitments?.length) {
      return [];
    }

    const filteredSponsorships = commitments.filter(
      (commitment) =>
        commitment.status === 'Active' &&
        (commitment.commitmentType === GraphQLCommitmentType.ChildSponsorship ||
          commitment.commitmentType ===
            GraphQLCommitmentType.LivingSponsorship),
    );

    // Sort children alphabetically by participant name
    return filteredSponsorships.sort((a, b) => {
      const nameA = a.participant?.name?.toLowerCase() ?? '';
      const nameB = b.participant?.name?.toLowerCase() ?? '';
      return nameA.localeCompare(nameB);
    });
  }, [commitments]);

  const isShowingSkeletons = (isLoading || isFetching || isPending) && !isError;
  const isShowingSponsorships = !isShowingSkeletons && !!sponsorships?.length;
  const hasNoSponsorships =
    !isShowingSkeletons && !isError && !sponsorships.length;
  const isShowingError = !isShowingSkeletons && isError;

  return {
    isShowingSkeletons,
    hasError: isError,
    sponsorships,
    isShowingSponsorships,
    isShowingError,
    hasNoSponsorships,
  };
};

/**
 * Displays list of active sponsorships sorted alphabetically by child name
 */
export const SponsorshipsList = () => {
  const {
    hasNoSponsorships,
    isShowingError,
    isShowingSkeletons,
    isShowingSponsorships,
    sponsorships,
  } = useComponentData();
  const { t } = useTranslation(['my', 'common']);

  return (
    <div className="SponsorshipsList max-w-md flex flex-col gap-comfy-xl">
      {/* Loading state with skeletons */}
      {isShowingSkeletons && <ItemCardSkeleton />}

      {/* Success state - display sponsorships */}
      {isShowingSponsorships && (
        <div className="SponsorshipsList__group flex flex-col gap-comfy-xs max-w-container-lg">
          {sponsorships.map((sponsorship) => (
            <SponsorshipCard
              key={sponsorship.internalId}
              commitment={sponsorship}
              href={`/my-sponsorships/${sponsorship.participant?.globalId}`}
            />
          ))}
        </div>
      )}

      {/* TODO: We have designs to implement */}
      {hasNoSponsorships && (
        <>
          <h3 className="heading-sm mb-snug-xl">
            {t('sponsorships.noSponsorships.title')}
          </h3>

          <p className="body-sm label-neutral-mild">
            {t('sponsorships.noSponsorships.message')}
          </p>
        </>
      )}

      {/* Error state */}
      {isShowingError && (
        <Alert
          variant="negative"
          title={t('sponsorships.queryError.title')}
          message={t('sponsorships.queryError.message')}
          action={
            <a href={t('my:ctas.contactSupport.url')}>
              {t('my:ctas.contactSupport.text')}
            </a>
          }
        />
      )}
    </div>
  );
};
